/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';

/**
 * @typedef {import('../../models/health.model.js').HealthRecord} HealthRecord
 */

/**
 * Health Repository
 * Handles CRUD operations for health records (wounds, diseases, injuries, treatments, vaccinations)
 */
export const healthRepository = {
  /**
   * Create a new health record
   * @param {Omit<HealthRecord, 'id'|'createdAt'|'updatedAt'>} healthData
   * @returns {Promise<number>}
   */
  async create(healthData) {
    const sql = 'INSERT INTO health_records (goat_id, type, title, description, notes, measured_at) VALUES (?, ?, ?, ?, ?, ?)';
    const result = await databaseService.run(sql, [
      healthData.goatId,
      healthData.type,
      healthData.title,
      healthData.description,
      healthData.notes || null,
      healthData.measuredAt
    ]);
    return result?.changes?.lastId ?? null;
  },

  /**
   * Find health record by ID
   * @param {number} id
   * @returns {Promise<HealthRecord|null>}
   */
  async findById(id) {
    const sql = 'SELECT * FROM health_records WHERE id = ?';
    const result = await databaseService.query(sql, [id]);
    if (!result.values || result.values.length === 0) return null;
    
    const row = result.values[0];
    return {
      id: row.id,
      goatId: row.goat_id,
      type: row.type,
      title: row.title,
      description: row.description,
      notes: row.notes,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  },

  /**
   * Find all health records for a specific goat
   * @param {number} goatId
   * @returns {Promise<HealthRecord[]>}
   */
  async findByGoat(goatId) {
    const sql = 'SELECT * FROM health_records WHERE goat_id = ? ORDER BY measured_at DESC, created_at DESC';
    const result = await databaseService.query(sql, [goatId]);
    
    return (result.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      type: row.type,
      title: row.title,
      description: row.description,
      notes: row.notes,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  },

  /**
   * Find wounds, diseases, and injuries for a goat
   * @param {number} goatId
   * @returns {Promise<HealthRecord[]>}
   */
  async findWoundsByGoat(goatId) {
    const sql = "SELECT * FROM health_records WHERE goat_id = ? AND type IN ('wound', 'disease', 'injury') ORDER BY measured_at DESC, created_at DESC";
    const result = await databaseService.query(sql, [goatId]);
    
    return (result.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      type: row.type,
      title: row.title,
      description: row.description,
      notes: row.notes,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  },

  /**
   * Find treatments and vaccinations for a goat
   * @param {number} goatId
   * @returns {Promise<HealthRecord[]>}
   */
  async findTreatmentsByGoat(goatId) {
    const sql = "SELECT * FROM health_records WHERE goat_id = ? AND type IN ('treatment', 'vaccination') ORDER BY measured_at DESC, created_at DESC";
    const result = await databaseService.query(sql, [goatId]);
    
    return (result.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      type: row.type,
      title: row.title,
      description: row.description,
      notes: row.notes,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  },

  /**
   * Find all health records
   * @returns {Promise<HealthRecord[]>}
   */
  async findAll() {
    const sql = 'SELECT * FROM health_records ORDER BY measured_at DESC, created_at DESC';
    const result = await databaseService.query(sql, []);
    
    return (result.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      type: row.type,
      title: row.title,
      description: row.description,
      notes: row.notes,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  },

  /**
   * Update a health record
   * @param {number} id
   * @param {Partial<HealthRecord>} updates
   * @returns {Promise<void>}
   */
  async update(id, updates) {
    const sql = 'UPDATE health_records SET type = ?, title = ?, description = ?, notes = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?';
    await databaseService.run(sql, [
      updates.type,
      updates.title,
      updates.description,
      updates.notes || null,
      updates.measuredAt,
      id
    ]);
  },

  /**
   * Delete a health record
   * @param {number} id
   * @returns {Promise<void>}
   */
  async delete(id) {
    await databaseService.run('DELETE FROM health_records WHERE id = ?', [id]);
  },

  /**
   * Delete all health records for a specific goat
   * @param {number} goatId
   * @returns {Promise<void>}
   */
  async deleteByGoat(goatId) {
    await databaseService.run('DELETE FROM health_records WHERE goat_id = ?', [goatId]);
  }
};
